import select from 'dom-select';

export function isTouch() {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0
  );
}

export function isDesktop(width = 1280) {
  return !isTouch() && window.innerWidth >= width;
}

export function isHidden(el: HTMLElement) {
  const style = window.getComputedStyle(el);
  return style.display === 'none';
}

export function setCookie(name: string,value: string, days?: number) {
  let expires = "";
  if (days) {
    let date = new Date();
    date.setTime(date.getTime() + (days*24*60*60*1000));
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "")  + expires + "; path=/";
}

export function getCookie(name: string) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for(let i=0;i < ca.length;i++) {
    let c = ca[i];
    while (c.charAt(0)==' ') c = c.substring(1,c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
  }
  return null;
}

export function eraseCookie(name: string) {
  document.cookie = name +'=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

export function getScreenRefreshRate(
  callback: (fps: number, coll: number[]) => void,
  runIndefinitely: boolean = false,
) {
  let requestId: number = 0;
  let callbackTriggered = false;
  runIndefinitely = runIndefinitely || false;

  if (!window.requestAnimationFrame) {
    window.requestAnimationFrame =
      (window as any).mozRequestAnimationFrame || (window as any).webkitRequestAnimationFrame;
  }

  let DOMHighResTimeStampCollection: number[] = [];

  let triggerAnimation = function (DOMHighResTimeStamp: number) {
    DOMHighResTimeStampCollection.unshift(DOMHighResTimeStamp);

    if (DOMHighResTimeStampCollection.length > 10) {
      let t0 = DOMHighResTimeStampCollection.pop() || 0;
      let fps = Math.floor((1000 * 10) / (DOMHighResTimeStamp - t0));

      if (!callbackTriggered) {
        callback.call(undefined, fps, DOMHighResTimeStampCollection);
      }

      callbackTriggered = !runIndefinitely;
    }

    requestId = window.requestAnimationFrame(triggerAnimation);
  };

  window.requestAnimationFrame(triggerAnimation);

  // Stop after half second if it shouldn't run indefinitely
  if (!runIndefinitely) {
    window.setTimeout(function () {
      window.cancelAnimationFrame(requestId);
      requestId = 0;
    }, 500);
  }
}

export function trimCharacter(str: string, chars: string) {
  return str.replace(new RegExp(`^[${chars}]+|[${chars}]+$`, 'g'), '');
}

export function replaceBodyClassName(name: string, prefix = 'page-') {
  const classes = document.body.className
    .split(' ')
    .filter((clName) => clName.indexOf(prefix) === -1);
  if (name) {
    classes.push(`${prefix}${name}`);
  }
  document.body.className = classes.join(' ');
}

export function replaceBodyClassNameTransition(name: string, clear = false) {
  const classes = document.body.className
    .split(' ')
    .filter((clName) => clName.indexOf('transition-') === -1);
  if (!clear) {
    classes.push(`transition-${name}`);
  }
  document.body.className = classes.join(' ');
}

export function toKebabCase(str: string) {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

function pxToNumber(px: string) {
  return Number(px.replace('px', ''));
}

type Breakpoints = 'sm' | 'md' | 'to-md' | 'lg' | 'to-lg' | 'xl' | 'hd' | 'max';

export function breakpoint(width: Breakpoints) {
  const breakpoints = {
    sm: '',
    md: '(min-width: 768px)',
    'to-md': '(max-width: 767px)',
    lg: '(min-width: 1024px)',
    'to-lg': '(max-width: 1023px)',
    xl: '(min-width: 1366px)',
    hd: '(min-width: 1600px)',
    max: '(min-width: 2000px)',
  };
  return breakpoints[width];
}

export function breakpointCallback(
  width: Breakpoints,
  callback: ({ matches }: { matches: boolean }) => void,
) {
  const mql = window.matchMedia(breakpoint(width));
  mql.addEventListener('change', callback);
  callback({ matches: mql.matches });
}

export function isBreakpoint(width: Breakpoints) {
  return window.matchMedia(breakpoint(width)).matches;
}

export function checkHttp(url: string) {
  return url.indexOf('http') === 0 ? url : `https://${url}`;
}

export function hmr() {
  // @ts-ignore
  if (import.meta.hot) {
    // @ts-ignore
    import.meta.hot.accept(() => {
      console.log('HMR');
    });
  }
}

export function parseHtml(html: string, selector?: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return select(selector || 'body', doc as unknown as HTMLElement) || null;
}

export async function getHtmlFromUrl(url: string, selector?: string) {
  try {
    const res = await fetch(url);
    const html = await res.text();
    return parseHtml(html, selector);
  } catch (_) {
    return null;
  }
}

export function getCrsf() {
  return fetch('/actions/blitz/csrf/json')
    .then((res) => res.json())
    .then(({ token }) => token as string)
    .catch();
}

export function toFixedNo(no: number|string, radix = 4) {
  return Number(Number(no).toFixed(radix))
}
